import { useState } from "react";
import { useParams } from "react-router-dom";
import { BarChart3, Globe, ShieldAlert, Target, Loader2, AlertCircle } from "lucide-react";
import ComparatorView from "@/components/agents/comparator-view";
import RiskDetectorView from "@/components/agents/risk-detector-view";
import EvaluatorView from "@/components/agents/evaluator-view";
import GeographicContextView from "@/components/agents/geographic-context-view";
import AgentExecutionHistory from "@/components/agents/agent-execution-history";
import { useAgentExecutions, type AgentExecution } from "@/hooks/use-agents";

type AgentTab = "comparador" | "riesgos" | "evaluador" | "contexto";

interface TabConfig {
  id: AgentTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType<{ licitacionId: string; initialResult?: any }>;
  agentName: string;
}

const tabs: TabConfig[] = [
  {
    id: "comparador",
    label: "Comparador",
    icon: BarChart3,
    component: ComparatorView,
    agentName: "comparator",
  },
  {
    id: "riesgos",
    label: "Riesgos",
    icon: ShieldAlert,
    component: RiskDetectorView,
    agentName: "risk_detector",
  },
  {
    id: "evaluador",
    label: "Evaluador",
    icon: Target,
    component: EvaluatorView,
    agentName: "evaluator",
  },
  // Mejora G (changelog cliente): agente de contexto geográfico/demográfico.
  {
    id: "contexto",
    label: "Contexto",
    icon: Globe,
    component: GeographicContextView,
    agentName: "geographic_context",
  },
];

export default function AgentesPage() {
  const { id: licitacionId } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<AgentTab>("comparador");
  const [selectedExecution, setSelectedExecution] = useState<AgentExecution | null>(null);

  // Obtener historial de ejecuciones. Filtramos `section_critic` porque no
  // tiene tab/visor en esta página (vive en el panel de propuestas) y al
  // hacer click en "Ver resultado" su shape no encaja con ninguna de las 3
  // vistas, lo que rompía el render con `Cannot read properties of undefined`.
  const { data: allExecutions = [], isLoading, isError } = useAgentExecutions(licitacionId ?? null);
  const executions = allExecutions.filter((ex) => ex.agent_name !== "section_critic");

  const activeTabConfig = tabs.find((tab) => tab.id === activeTab);
  const ActiveComponentType = activeTabConfig?.component || ComparatorView;

  // Pasar resultado del historial al componente activo
  const activeInitialResult = selectedExecution?.result ?? null;

  const handleViewResult = (execution: AgentExecution) => {
    setSelectedExecution(execution);
    // Cambiar al tab correspondiente según el tipo de agente
    const agentTabMap: Record<string, AgentTab> = {
      comparator: "comparador",
      comparador_requisitos: "comparador",
      risk_detector: "riesgos",
      detector_riesgos: "riesgos",
      evaluator: "evaluador",
      evaluador_probabilidad: "evaluador",
      geographic_context: "contexto",
    };
    const tab = agentTabMap[execution.agent_name];
    if (tab) setActiveTab(tab);
  };

  return (
    <div className="h-screen p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Agentes IA</h1>
        <p className="text-sm text-gray-500 mt-1">
          Licitación: {licitacionId}
        </p>
      </div>

      <div className="h-full grid grid-cols-12 gap-6">
        {/* Main Content - Tabs */}
        <div className="col-span-9 flex flex-col">
          {/* Tab Navigation */}
          <div className="bg-white rounded-lg border border-gray-200 mb-4">
            <div className="flex border-b border-gray-200">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setSelectedExecution(null); }}
                    className={`flex items-center space-x-2 px-6 py-4 border-b-2 transition-colors ${
                      isActive
                        ? "border-blue-500 text-blue-600 bg-blue-50"
                        : "border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-auto">
            <ActiveComponentType licitacionId={licitacionId!} initialResult={activeInitialResult} />
          </div>
        </div>

        {/* Sidebar - Historial */}
        <div className="col-span-3 h-full">
          {isError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-700">Error al cargar el historial de ejecuciones.</p>
            </div>
          ) : (
          <AgentExecutionHistory
            executions={executions}
            onViewResult={handleViewResult}
            isLoading={isLoading}
          />
          )}
        </div>
      </div>
    </div>
  );
}
